/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy at http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND.
 */
import { useMemo, useState } from 'react';
import { hotels } from './catalog.js';
import { formatMoney, STEP_UP_THRESHOLD } from './booking.js';
import {
  authenticate,
  confirmBooking,
  createBookingIntent,
  demoMode,
  endSession,
  requestEmailChallenge,
  verifyEmailChallenge,
} from './api.js';

function futureDate(offset) {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return date.toISOString().slice(0, 10);
}

const initialSelection = {
  hotelId: hotels[0].id,
  roomTypeId: hotels[0].roomTypes[0].id,
  checkIn: futureDate(14),
  checkOut: futureDate(17),
  guests: 2,
};

export default function App() {
  const [traveler, setTraveler] = useState();
  const [selection, setSelection] = useState(initialSelection);
  const [intent, setIntent] = useState();
  const [challenge, setChallenge] = useState();
  const [confirmation, setConfirmation] = useState();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState();
  const selectedHotel = useMemo(
    () => hotels.find((hotel) => hotel.id === selection.hotelId),
    [selection.hotelId],
  );
  const selectedRoom = useMemo(
    () => selectedHotel.roomTypes.find((room) => room.id === selection.roomTypeId),
    [selectedHotel, selection.roomTypeId],
  );

  async function run(action) {
    setBusy(true);
    setNotice(undefined);
    try {
      return await action();
    } catch (error) {
      setNotice({ kind: 'error', message: error.message });
      throw error;
    } finally {
      setBusy(false);
    }
  }

  async function handleSignIn(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      setTraveler(await run(() => authenticate(form.get('email'), form.get('password'))));
    } catch {}
  }

  async function handleQuote(event) {
    event.preventDefault();
    try {
      setIntent(await run(() => createBookingIntent(selection)));
      setChallenge(undefined);
      setConfirmation(undefined);
      setNotice({ kind: 'success', message: 'Your secure quote is ready.' });
    } catch {}
  }

  async function handleConfirm() {
    try {
      setConfirmation(await run(() => confirmBooking(intent.bookingIntentId)));
    } catch (error) {
      if (error.code !== 'STEP_UP_REQUIRED' && error.status !== 401) return;
      try {
        setChallenge(await run(() => requestEmailChallenge(intent.bookingIntentId)));
      } catch {}
    }
  }

  async function handleVerify(event) {
    event.preventDefault();
    const code = new FormData(event.currentTarget).get('code');
    try {
      await run(() => verifyEmailChallenge(challenge.challengeId, code));
      setNotice({ kind: 'success', message: 'Identity confirmed. Completing your booking…' });
      setConfirmation(await run(() => confirmBooking(intent.bookingIntentId)));
    } catch {}
  }

  async function handleSignOut() {
    try {
      await run(endSession);
      setTraveler(undefined);
      setIntent(undefined);
      setChallenge(undefined);
      setConfirmation(undefined);
    } catch {
      // Keep local state until Cognito confirms sign-out so the UI reflects the real session.
    }
  }

  function selectHotel(hotel) {
    setSelection((current) => ({
      ...current,
      hotelId: hotel.id,
      roomTypeId: hotel.roomTypes[0].id,
      guests: Math.min(current.guests, hotel.roomTypes[0].maxGuests),
    }));
    setIntent(undefined);
    setChallenge(undefined);
  }

  function selectRoom(event) {
    const roomTypeId = event.target.value;
    const room = selectedHotel.roomTypes.find((candidate) => candidate.id === roomTypeId);
    setSelection((current) => ({
      ...current,
      roomTypeId,
      guests: Math.min(current.guests, room.maxGuests),
    }));
  }

  function selectGuests(event) {
    setSelection((current) => ({
      ...current,
      guests: Number(event.target.value),
    }));
  }

  function selectCheckIn(event) {
    setSelection((current) => ({
      ...current,
      checkIn: event.target.value,
    }));
  }

  function selectCheckOut(event) {
    setSelection((current) => ({
      ...current,
      checkOut: event.target.value,
    }));
  }

  function resetBooking() {
    setIntent(undefined);
    setChallenge(undefined);
    setConfirmation(undefined);
  }

  return (
    <div className="page-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="AnyHotel home">
          <span className="brand-mark">A</span>
          <span>AnyHotel</span>
        </a>
        <div className="header-actions">
          <span className="demo-pill">{demoMode ? 'Synthetic demo' : 'AWS connected'}</span>
          {traveler && (
            <button className="text-button" onClick={handleSignOut} disabled={busy}>
              Sign out
            </button>
          )}
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div>
            <p className="eyebrow">Find your next stay</p>
            <h1>Good trips start with a room that feels right.</h1>
            <p className="hero-copy">
              Three distinct hotels, clear prices, and an extra identity check when your
              booking calls for it.
            </p>
          </div>
          <div className="security-card">
            <span className="shield" aria-hidden="true">✓</span>
            <div>
              <strong>Protected booking</strong>
              <p>Bookings over {formatMoney(STEP_UP_THRESHOLD)} require an email check.</p>
            </div>
          </div>
        </section>

        {!traveler ? (
          <section className="signin-panel">
            <div>
              <p className="eyebrow">Traveler access</p>
              <h2>Sign in to explore rooms</h2>
              <p>{demoMode
                ? 'Use any synthetic email. A password is optional in demo mode.'
                : 'Use a synthetic user from the deployed Cognito pool.'}</p>
            </div>
            <form className="signin-form" onSubmit={handleSignIn}>
              <label>
                Email
                <input
                  name="email"
                  type="email"
                  defaultValue="traveler@example.com"
                  required
                />
              </label>
              <label>
                Password
                <input name="password" type="password" required={!demoMode} />
              </label>
              <button className="primary-button" disabled={busy}>Continue</button>
            </form>
          </section>
        ) : (
          <>
            <section className="section-heading">
              <div>
                <p className="eyebrow">Welcome, {traveler.email}</p>
                <h2>Choose an AnyHotel</h2>
              </div>
              <p>All properties, people, rates, and confirmations are synthetic.</p>
            </section>
            <section className="hotel-grid" aria-label="Available hotels">
              {hotels.map((hotel) => (
                <button
                  className={[
                    'hotel-card',
                    hotel.accent,
                    selection.hotelId === hotel.id ? 'selected' : '',
                  ].join(' ')}
                  type="button"
                  key={hotel.id}
                  onClick={() => selectHotel(hotel)}
                >
                  <span className="hotel-art">
                    <span>{hotel.city.slice(0, 3).toUpperCase()}</span>
                  </span>
                  <span className="hotel-content">
                    <span className="eyebrow">{hotel.eyebrow}</span>
                    <strong>{hotel.name}</strong>
                    <span>{hotel.description}</span>
                    <b>From {formatMoney(hotel.roomTypes[0].nightlyRate)} / night</b>
                  </span>
                </button>
              ))}
            </section>

            <section className="booking-layout">
              <form className="booking-form" onSubmit={handleQuote}>
                <div>
                  <p className="eyebrow">Your stay</p>
                  <h2>{selectedHotel.name}</h2>
                </div>
                <div className="form-grid">
                  <label>
                    Room
                    <select value={selection.roomTypeId} onChange={selectRoom}>
                      {selectedHotel.roomTypes.map((room) => (
                        <option value={room.id} key={room.id}>
                          {room.name} · {formatMoney(room.nightlyRate)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Guests
                    <select value={selection.guests} onChange={selectGuests}>
                      {Array.from(
                        { length: selectedRoom.maxGuests },
                        (_, index) => index + 1,
                      ).map((count) => (
                        <option value={count} key={count}>{count}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Check in
                    <input
                      type="date"
                      value={selection.checkIn}
                      onChange={selectCheckIn}
                    />
                  </label>
                  <label>
                    Check out
                    <input
                      type="date"
                      value={selection.checkOut}
                      onChange={selectCheckOut}
                    />
                  </label>
                </div>
                <button className="primary-button" disabled={busy}>Get secure quote</button>
              </form>

              <aside className="summary-card" aria-live="polite">
                {!intent && (
                  <div className="empty-summary"><span className="key-icon">⌁</span>
                    <h3>Your quote will appear here</h3>
                    <p>AnyHotel calculates the total on the server before confirmation.</p>
                  </div>
                )}
                {intent && !confirmation && (
                  <>
                    <p className="eyebrow">Quote summary</p>
                    <h3>{intent.roomType.name}</h3>
                    <dl>
                      <div>
                        <dt>{intent.nights} nights</dt>
                        <dd>
                          {formatMoney(intent.roomType.nightlyRate.value * intent.nights)}
                        </dd>
                      </div>
                      <div>
                        <dt>Taxes</dt>
                        <dd>
                          {formatMoney(
                            intent.total.value
                              - intent.roomType.nightlyRate.value * intent.nights,
                          )}
                        </dd>
                      </div>
                      <div className="total">
                        <dt>Total</dt>
                        <dd>{formatMoney(intent.total)}</dd>
                      </div>
                    </dl>
                    <div className={`risk-note ${intent.stepUpRequired ? 'step-up' : ''}`}>
                      <span>{intent.stepUpRequired ? '✦' : '✓'}</span>
                      <p>
                        <strong>
                          {intent.stepUpRequired ? 'Email check required' : 'Ready to confirm'}
                        </strong>
                        {intent.stepUpRequired
                          ? ' We will verify your identity before booking.'
                          : ' This stay is within the standard booking limit.'}
                      </p>
                    </div>
                    {!challenge && (
                      <button
                        className="primary-button full"
                        onClick={handleConfirm}
                        disabled={busy}
                      >
                        Confirm booking
                      </button>
                    )}
                    {challenge && (
                      <form className="challenge-form" onSubmit={handleVerify}>
                        <h3>Check your email</h3>
                        <p>We sent a six-digit code to {challenge.maskedDestination}.</p>
                        {challenge.demoCode && (
                          <p className="demo-code">
                            Demo email code <strong>{challenge.demoCode}</strong>
                          </p>
                        )}
                        <label>
                          Verification code
                          <input
                            name="code"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]{6}"
                            maxLength="6"
                            placeholder="000000"
                            required
                          />
                        </label>
                        <button className="primary-button full" disabled={busy}>
                          Verify and book
                        </button>
                      </form>
                    )}
                  </>
                )}
                {confirmation && (
                  <div className="confirmation">
                    <span className="confirmation-mark">✓</span>
                    <p className="eyebrow">Booking confirmed</p>
                    <h3>You are going to {intent.hotel.city}.</h3>
                    <p>Confirmation <strong>{confirmation.bookingId}</strong></p>
                    <button className="secondary-button" onClick={resetBooking}>
                      Plan another stay
                    </button>
                  </div>
                )}
              </aside>
            </section>
          </>
        )}
        {notice && (
          <div
            className={`notice ${notice.kind}`}
            role={notice.kind === 'error' ? 'alert' : 'status'}
          >
            {notice.message}
          </div>
        )}
      </main>
      <footer>
        <span>AnyHotel Company</span>
        <span>Reference implementation · Synthetic data only</span>
      </footer>
    </div>
  );
}
